<template>
  <div>
    <button class="btn btn-outline-primary" @click="buttonClick">
      <fa :icon="buttonIcon" /><span class="ml-2">{{ buttonText }}</span>
    </button>
    <!-- Modal -->
    <div ref="modal" class="modal fade" tabindex="-1" role="dialog" aria-labelledby="modal" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header border-0">
            <h5 class="modal-title text-capitalize">{{ buttonText }}</h5>
            <button type="button" class="close" aria-label="Close" data-dismiss="modal">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <ErrorSummary :error="error" />
            <div class="alert alert-warning text-small" v-if="hasIncompleteInterviews">
              <fa icon="exclamation-triangle" />
              Please note, by closing this case, links to incomplete interviews will no longer be accessible to interviewees.
            </div>
            <form ref="form" class="needs-validation" :class="formWasValidated ? 'was-validated' : ''" novalidate @submit.prevent>
              <div class="form-group">
                <label for="closeDate">Close Date</label>
                <v-date-picker
                  :attributes="vCalendarAttributes"
                  :model-config="vCalendarModelConfig"
                  :minDate="vCalendarMinDate"
                  :maxDate="vCalendarMaxDate"
                  v-model="closeDate"
                />
                <small class="form-text text-muted">Close date must be between then creation of the investigation and today.</small>
              </div>
              <div class="form-group">
                <label>Outcome</label>
                <select class="form-control" v-model="outcomeCode" :disabled="loadingMessage" required>
                  <option value selected hidden disabled>Select a test type</option>
                  <option v-for="o in investigationOutcomes" v-bind:key="o.code" v-bind:value="o.code">
                    {{ o.shortDescription }}
                  </option>
                </select>
                <div class="invalid-feedback">Please select an outcome.</div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-primary" @click="submit">Submit</button>
            <button type="button" class="btn btn-primary" data-dismiss="modal">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import $ from "jquery";
import formatter from "@/utils/formatter";
import ErrorSummary from "@/components/ErrorSummary.vue";

export default {
  components: {
    ErrorSummary,
  },
  props: {
    isLoading: Boolean,
    investigation: Object,
  },
  data() {
    return {
      successMessage: null,
      loadingMessage: null,
      formWasValidated: false,
      error: null,

      investigationOutcomes: [],
      interviews: [],

      vCalendarAttributes: [],
      vCalendarModelConfig: {},

      closeDate: new Date(),
      outcomeCode: null,
    };
  },
  computed: {
    formatter: () => formatter,
    isSelfLoading() {
      return !!this.loadingMessage;
    },
    isOpen() {
      return this.investigation.statusCode === "open";
    },
    isClosed() {
      return this.investigation.statusCode === "closed";
    },
    buttonText() {
      return this.isClosed ? "Re-Open Investigation" : "Close Investigation";
    },
    buttonIcon() {
      return this.isClosed ? "folder-open" : "times";
    },
    vCalendarMinDate() {
      try {
        return new Date(this.investigation.createdDate);
      } catch {
        return new Date();
      }
    },
    vCalendarMaxDate() {
      return new Date();
    },
    adjustedCloseData() {
      if (this.closeDate && this.closeDate < this.vCalendarMinDate) return this.vCalendarMinDate;
      return this.closeDate;
    },
    hasIncompleteInterviews() {
      var interviews = this.interviews.filter(i => i.organizationReportStatusCode === "sent-by-email");
      return !!interviews.length;
    },
  },
  watch: {
    investigation() {
      this.loadingMessage = "Loading...";
      const promises = [this.getOrganizationOutcomes()];
      Promise.all(promises).then(() => (this.loadingMessage = null));
    },
  },
  methods: {
    getOrganizationOutcomes() {
      return this.$axios
        .get(`/v1/organizations/${this.investigation.organizationId}/investigations/outcomes`)
        .then(res => (this.investigationOutcomes = res.data.items))
        .catch(err => (this.error = err));
    },
    getInterviews() {
      this.$axios
        .get(`/v1/organizations/${this.investigation.organizationId}/investigations/${this.investigation.investigationId}/interviews?take=100&skip=0`)
        .then(res => (this.interviews = res.data.items))
        .catch(err => (this.error = err));
    },
    buttonClick() {
      if (this.isOpen) {
        this.getInterviews();
        this.showModal();
        return;
      }

      if (this.isClosed) {
        this.openInvestigation();
        return;
      }
    },
    showModal() {
      $(this.$refs.modal).modal("show");
    },
    closeModal() {
      $(this.$refs.modal).modal("hide");
    },
    reset() {
      this.loadingMessage = false;
      this.formWasValidated = false;
      this.error = null;
    },
    submit() {
      var payload = {
        closeDate: this.adjustedCloseData.toISOString(),
        outcomeCode: this.outcomeCode,
      };

      // reset
      this.reset();

      // validations
      if (!this.$refs.form.checkValidity()) {
        this.formWasValidated = true;
        return;
      }

      // api
      this.loadingMessage = "Loading...";
      this.$axios
        .post(`/v1/organizations/${this.investigation.organizationId}/investigations/${this.investigation.investigationId}/close`, payload)
        .then(() => {
          this.closeModal();
          this.$emit("reload");
        })
        .catch(err => {
          if (err.response.status === 404) this.$router.push(`/organizations/${this.investigation.organizationId}/investigations`);
          this.error = err;
        })
        .finally(() => (this.loadingMessage = null));
    },
    openInvestigation() {
      // api
      this.loadingMessage = "Loading...";
      this.$axios
        .post(`/v1/organizations/${this.investigation.organizationId}/investigations/${this.investigation.investigationId}/open`)
        .then(() => {
          this.$emit("reload");
        })
        .catch(err => {
          if (err.response.status === 404) this.$router.push(`/organizations/${this.investigation.organizationId}/investigations`);
          this.error = err;
        })
        .finally(() => (this.loadingMessage = null));
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/base.scss";

.modal,
.modal-content,
.modal-body {
  overflow: visible;
  overflow-y: visible;
}

.vc-popover-content-wrapper {
  z-index: 1080;
}
</style>
